import { useLazyQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'

import { SelectTags } from 'components/v2/SelectTags'
import { GTBusinessSiteSuggestion, GTBusinessSiteSuggestionsResponse } from 'gql/types.generated'
import { ISelectTagsProps } from 'components/v2/SelectTags/types'
import { GET_BUSINESS_SITES_SUGGESTIONS } from 'features/BusinessSites/queries-legacy'
import { useDebouncedQuery } from 'components/v2/Autocomplete'

export interface IBusinessSiteAutocomplete {
  id: string
  name: string
  clientId?: string
}

interface IMultiBusinessSiteAutocompleteProps extends Omit<ISelectTagsProps, 'onChange' | 'items' | 'getTagLabel'> {
  onChange: (sites: IBusinessSiteAutocomplete[]) => void
  businessClientIds?: string[]
}

export const MultiBusinessSiteAutocomplete = ({
  onChange,
  label,
  placeholder,
  values,
  businessClientIds,
  ...props
}: IMultiBusinessSiteAutocompleteProps): ReactElement => {
  const [pattern, setPattern] = useState('')
  const [getBusinessSites, { data, loading, error }] = useLazyQuery<{
    businessSiteSuggestions: GTBusinessSiteSuggestionsResponse
  }>(GET_BUSINESS_SITES_SUGGESTIONS)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getBusinessSites,
    pattern,
    extraInputs: {
      businessClientIDs: businessClientIds || [],
      isActive: true
    }
  })

  const selectedSitesIds = values.map(({ id }) => id)
  const shownItems =
    data?.businessSiteSuggestions?.items.filter((site) => {
      return !selectedSitesIds.includes(site.id)
    }) || []

  const handleChange = (sites: (GTBusinessSiteSuggestion & IBusinessSiteAutocomplete)[], pattern = ''): void => {
    setPattern(pattern)
    onChange(
      sites.map((site) => ({
        id: site.id,
        name: site.name,
        clientId: site.clientId ? site.clientId : site.businessClient?.id || null
      }))
    )
  }

  return (
    <SelectTags
      placeholder={placeholder}
      values={values}
      label={label}
      onChange={handleChange}
      items={shownItems}
      getTagLabel={(site: GTBusinessSiteSuggestion): string => site.name}
      loading={loading || isDebouncing}
      error={error}
      shouldShowItems={shouldShowItems}
      {...props}
    />
  )
}
